
/**
 * Campaign Filters
 */
import mountFarloRangeCalendar from './rangeCalendar';
import {
	initFilterBar,
	getInlineObj,
	toQS,
	cleanParams,
	updateAllFiltersCount,
	updateClearChip,
	wireCalendarEnable,
	paintChoiceActives,
	has3Chars
} from './filtersCore';
import { createFilterTracker } from './filterAnalytics';

function snapshotPanel(panel) {
	if (!panel) {
		return;
	}
	const snap = {
		checks: Array.from(panel.querySelectorAll('input[type="checkbox"], input[type="radio"]'))
			.map((i) => ({ name: i.name, value: i.value, checked: i.checked })),
		fields: ['from', 'to', 'max'].map((n) => {
			const el = panel.querySelector(`[name="${ n }"]`);
			return { name: n, value: el ? el.value : '' };
		})
	};
	panel.__snap = snap;
}

function restorePanel(panel) {
	const s = panel?.__snap;
	if (!s) {
		return;
	}
	s.checks.forEach(({ name, value, checked }) => {
		panel.querySelectorAll(`input[name="${ CSS.escape(name) }"]`).forEach((i) => {
			if (i.type === 'radio') {
				i.checked = (i.value === value) && checked;
			} else if (i.type === 'checkbox') {
				if (i.value === value) {
					i.checked = checked;
				}
			}
		});
	});
	s.fields.forEach(({ name, value }) => {
		const el = panel.querySelector(`[name="${ CSS.escape(name) }"]`);
		if (el) {
			el.value = value;
		}
	});
}

export default function initCampaignFilters() {
	const bar = document.querySelector('[data-campaign-filterbar]');
	if (!bar) {
		return;
	}

	const form = bar.closest('form') || bar;
	const list = document.querySelector('[data-results]');
	const endpoint = bar.dataset.endpoint || '/wp-json/olt/v1/campaign-shows';

	// Campaign context from data attributes
	const brand = bar.dataset.brand || '';
	const phase = bar.dataset.phase || '';
	const pageSlug = bar.dataset.pageSlug || '';

	const filterTracker = createFilterTracker('campaign');

	// 'shows' is included in MULTI so getInlineObj() collects multiple
	// hidden inputs with name="shows[]" (created by the show search chips) into an array.
	// 'age_guide' is single-select (radio group), so it is intentionally NOT
	// in MULTI — getInlineObj() returns it as a scalar string and the REST
	// endpoint's CSV parser handles a single value identically to a CSV list.
	const MULTI = new Set(['genre', 'time', 'shows']);

	const DEFAULTS = {
		q: '', from: '', to: '', time: '',
		max: '', shows: [], sort: 'name_asc'
	};

	const SORT_MAP = {
		'name-atz': 'name_asc', 'name-zta': 'name_desc',
		'date-old': 'date_old', 'date-new': 'date_new',
		'price-low': 'price_low', 'price-high': 'price_high'
	};
	const normaliseSort = (v) => SORT_MAP[ v ] || v || 'name_asc';

	// Keys that must not appear in the browser URL.
	const CONTEXT_KEYS = ['brand', 'phase', 'page_slug'];

	const setTriggerActive = (key, active) => {
		const btn = bar.querySelector(`[data-open="${ key }"]`);
		if (!btn) {
			return;
		}
		btn.classList.toggle('is-active', !!active);
		btn.setAttribute('aria-pressed', active ? 'true' : 'false');
	};

	const isPriceActive = () => {
		// Single-thumb max-only slider; min axis removed across all filter bars.
		const max = form.querySelector('input[type="hidden"][name="max"]:not([disabled])')?.value ?? '';
		return max !== '' && Number(max) < 100;
	};
	const isTimeActive = () => !!form.querySelector('input[name="time[]"]:checked');
	const isGenreActive = () => !!form.querySelector('input[name="genre[]"]:checked');
	const isAgeGuideActive = () => !!form.querySelector('input[name="age_guide"]:checked');

	// Repaint the Age Guide chip text from the currently selected radio.
	// Falls back to "Age Guide" when nothing is selected. The " x" suffix
	// signals the chip is clickable to clear (via Cancel-when-active in the
	// popover) and matches the style used by genre/time chips.
	const updateAgeGuidePillLabel = () => {
		const $label = bar.querySelector('[data-age-guide-label]');
		if (!$label) {
			return;
		}
		const checked = form.querySelector('input[name="age_guide"]:checked');
		const selectedLabel = checked?.dataset?.label || '';
		$label.textContent = selectedLabel ? (selectedLabel + ' x') : 'Age Guide';
	};
	const isDateActive = () => {
		const from = form.querySelector('input[type="hidden"][name="from"]:not([disabled])')?.value || '';
		const to = form.querySelector('input[type="hidden"][name="to"]:not([disabled])')?.value || '';
		return !!(from || to);
	};

	const setAllTriggersFromForm = () => {
		setTriggerActive('price', isPriceActive());
		setTriggerActive('time', isTimeActive());
		setTriggerActive('genre', isGenreActive());
		setTriggerActive('age_guide', isAgeGuideActive());
		setTriggerActive('date', isDateActive());
		updateAgeGuidePillLabel();
	};

	// Helper to get or create an enabled hidden input at form level.
	const getOrCreateHidden = (name) => {
		let inp = form.querySelector(`input[type="hidden"][name="${ name }"]:not([disabled])`);
		if (!inp) {
			inp = document.createElement('input');
			inp.type = 'hidden';
			inp.name = name;
			form.appendChild(inp);
		}
		return inp;
	};

	const paramsFromForm = () => {
		const obj = getInlineObj(form, MULTI);

		// Remove campaign context - these come from data attributes, not form.
		CONTEXT_KEYS.forEach((k) => delete obj[ k ]);

		if (Array.isArray(obj.genre)) {
			obj.genre = [...new Set(obj.genre.map((value) => String(value).trim()).filter(Boolean))].sort();
		}
		if (Array.isArray(obj.time)) {
			obj.time = obj.time.filter(Boolean);
		}
		// age_guide is single-select (scalar string). Strip empty values so a
		// URL like ?age_guide= doesn't survive into the next request.
		if (typeof obj.age_guide === 'string' && obj.age_guide.trim() === '') {
			delete obj.age_guide;
		}


		if (obj.from && !obj.to) {
			obj.to = obj.from;
		}
		obj.sort = obj.sort || 'name_asc';

		return obj;
	};

	// Custom renderer that adds campaign context to API calls only.
	const cache = new Map();
	let inflight = null;

	const render = async (pushHistory = true) => {
		if (!list) {
			return;
		}

		const base = cleanParams(paramsFromForm(), DEFAULTS);
		if (base.sort) {
			base.sort = normaliseSort(base.sort);
		}

		// Only send the text search if it is at least 2 characters.
		const searchEl = form.querySelector('input[name="q"]');
		if (searchEl) {
			const raw = searchEl.value.trim();
			if (raw.replace(/\s+/g, '').length >= 2) {
				base.q = raw;
			} else {
				delete base.q;
			}
		}

		// Emit dataLayer events for any filters that changed this render.
		filterTracker.track(base);

		// URL uses only filter params (no campaign context).
		const qs = toQS(base);

		if (pushHistory) {
			const url = qs ? `${ location.pathname }?${ qs }` : location.pathname;
			history.pushState({ qs }, '', url);
		}

		if (cache.has(qs)) {
			const cached = cache.get(qs);
			list.innerHTML = cached.html || '<p class="no-results">No shows match your filters.</p>';
			updateHydrationElement(cached.total, base);
			return cached;
		}

		const CHUNK_SIZE = 20;
		const apiParams = {
			...base,
			brand,
			phase,
			page_slug: pageSlug,
			offset: 0,
			limit: CHUNK_SIZE
		};
		const apiQs = toQS(apiParams);

		if (inflight) {
			inflight.abort();
		}
		inflight = new AbortController();

		try {
			list.setAttribute('aria-busy', 'true');
			const res = await fetch(`${ endpoint }?${ apiQs }`, {
				credentials: 'same-origin',
				signal: inflight.signal
			});
			const data = await res.json();
			cache.set(qs, data);
			list.innerHTML = data.html || '<p class="no-results">No shows match your filters.</p>';

			updateHydrationElement(data.total || 0, base);

			return data;
		} catch (err) {
			if (err.name === 'AbortError') {
				return;
			}
			list.innerHTML = '<p class="no-results">No shows match your filters.</p>';
		} finally {
			list.removeAttribute('aria-busy');
			inflight = null;
		}
	};

	// Update or create the hydration element for infinite scroll of remaining results.
	const updateHydrationElement = (total, filterParams) => {
		const CHUNK_SIZE = 20;

		document.querySelector('[data-campaign-hydrate]')?.remove();
		document.querySelector('[data-sentinel]')?.remove();

		if (total > CHUNK_SIZE) {
			const hydrate = document.createElement('div');
			hydrate.setAttribute('data-campaign-hydrate', '');
			hydrate.setAttribute('data-endpoint', endpoint);
			hydrate.setAttribute('data-brand', brand);
			hydrate.setAttribute('data-phase', phase);
			hydrate.setAttribute('data-page-slug', pageSlug);
			hydrate.setAttribute('data-offset', String(CHUNK_SIZE));
			hydrate.setAttribute('data-chunk', String(CHUNK_SIZE));
			hydrate.setAttribute('data-total', String(total));
			hydrate.setAttribute('data-mode', 'scroll');

			if (filterParams && Object.keys(filterParams).length > 0) {
				hydrate.setAttribute('data-filter-params', JSON.stringify(filterParams));
			}

			hydrate.innerHTML = '<div class="loading-placeholder" style="text-align: center; padding: 20px;">Loading more shows...</div>';

			const sentinel = document.createElement('div');
			sentinel.setAttribute('data-sentinel', '');
			sentinel.style.height = '100px';
			sentinel.style.marginTop = '50px';

			list.parentElement.appendChild(hydrate);
			list.parentElement.appendChild(sentinel);

			if (typeof window.hydrateCampaignShows === 'function') {
				window.hydrateCampaignShows();
			}
		}
	};

	// Back/forward navigation.
	window.addEventListener('popstate', () => render(false));

	// Seed hidden inputs from URL params before initFilterBar runs.
	// 'min' intentionally absent: the price slider is single-thumb max-only,
	// so a legacy ?min=… in a deep link should not re-introduce a form field.
	const urlParams = new URLSearchParams(location.search);
	['from', 'to', 'max', 'sort'].forEach((key) => {
		const val = urlParams.get(key);
		if (val !== null) {
			getOrCreateHidden(key).value = val;
		}
	});

	// Wire up the filter bar.
	initFilterBar({
		bar, form, list, endpoint,
		defaults: DEFAULTS, multi: MULTI, normaliseSort, paramsFromForm,
		render,
		onBeforeOpenScope(key, { modal, panel }) {
			if (panel) {
				const pKey = panel.getAttribute('data-panel') || '';
				let activeAtOpen = false;

				if (pKey === 'price') {
					activeAtOpen = isPriceActive();
				} else if (pKey === 'time') {
					activeAtOpen = isTimeActive();
				} else if (pKey === 'genre') {
					activeAtOpen = isGenreActive();
				} else if (pKey === 'age_guide') {
					activeAtOpen = isAgeGuideActive();
				} else if (pKey === 'date') {
					activeAtOpen = isDateActive();
				}

				panel.setAttribute('data-active', activeAtOpen ? '1' : '0');
				snapshotPanel(panel);
			}

			// Date popover calendar.
			if (key === 'date' && panel) {
				const container = panel.querySelector('[data-range-calendar],[data-range-calendar="popover"]');
				const from = form.querySelector('input[name="from"]');
				const to = form.querySelector('input[name="to"]');
				if (container && !container.dataset.calBound && from && to) {
					wireCalendarEnable(panel, container, from, to, () => {
						mountFarloRangeCalendar(container, from, to, 1);
					});
				}
			}

			// All Filters modal.
			if (key === 'all' && modal) {
				const container = modal.querySelector('[data-range-calendar="modal"]');
				const mFrom = modal.querySelector('[data-modal-from]');
				const mTo = modal.querySelector('[data-modal-to]');
				const inlineFrom = form.querySelector('input[name="from"]')?.value || '';
				const inlineTo = form.querySelector('input[name="to"]')?.value || '';

				if (mFrom) {
					mFrom.value = inlineFrom;
				}
				if (mTo) {
					mTo.value = inlineTo;
				}

				if (container && !container.dataset.calBound && mFrom && mTo) {
					wireCalendarEnable(modal, container, mFrom, mTo, () => {
						const mq = matchMedia('(min-width:768px)');
						const mount = () => {
							container.innerHTML = '';
							mountFarloRangeCalendar(container, mFrom, mTo, mq.matches ? 2 : 1);
						};
						mount();
						mq.addEventListener?.('change', mount) || mq.addListener?.(mount);
					});
				}

				const timeChecked = Array.from(form.querySelectorAll('input[name="time[]"]:checked')).map((i) => i.value);
				modal.querySelectorAll('[data-modal-time]').forEach((c) => {
					c.checked = timeChecked.includes(c.value);
				});

				const genresChecked = Array.from(form.querySelectorAll('input[name="genre[]"]:checked')).map((i) => i.value);
				modal.querySelectorAll('[data-modal-genre]').forEach((c) => {
					c.checked = genresChecked.includes(c.value);
				});

				paintChoiceActives(modal);
			}
		},
		onAfterUiRefresh() {
			setAllTriggersFromForm();
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
			const params = cleanParams(paramsFromForm(), DEFAULTS);
			updateClearChip(bar, params, { sort: 'name_asc' });
		}
	});

	// Modal Apply handler.
	bar.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-submit]');
		if (!btn) {
			return;
		}

		const modalEl = btn.closest('dialog[data-modal="all"]');
		if (!modalEl) {
			return;
		}

		e.preventDefault();
		e.stopImmediatePropagation();

		const mFrom = modalEl.querySelector('[data-modal-from]');
		const mTo = modalEl.querySelector('[data-modal-to]');
		getOrCreateHidden('from').value = mFrom?.value || '';
		getOrCreateHidden('to').value = mTo?.value || '';

		const modalTime = Array.from(modalEl.querySelectorAll('[data-modal-time]:checked')).map((c) => c.value);
		form.querySelectorAll('input[name="time[]"]').forEach((c) => {
			c.checked = modalTime.includes(c.value);
		});

		const modalGenre = Array.from(modalEl.querySelectorAll('[data-modal-genre]:checked')).map((c) => c.value);
		form.querySelectorAll('input[name="genre[]"]').forEach((c) => {
			c.checked = modalGenre.includes(c.value);
		});

		const wrap = modalEl.querySelector('[data-modal-price][data-price-wrap]');
		if (wrap) {
			// Single-thumb slider: only max is synced. min is implicit 0.
			const mx = wrap.querySelector('input[name="max"]');
			getOrCreateHidden('max').value = String(mx?.value ?? '');
		}

		paintChoiceActives(document);
		modalEl.close?.();
		setAllTriggersFromForm();
		updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
		const params = cleanParams(paramsFromForm(), DEFAULTS);
		updateClearChip(bar, params, { sort: 'name_asc' });
		render();
	}, { capture: true });

	// Popover Apply/Clear handlers.
	bar.addEventListener('click', (e) => {
		const panel = e.target.closest('.popover__panel');
		if (!panel) {
			return;
		}

		const applyBtn = e.target.closest('[data-submit]');
		const clearBtn = e.target.closest('[data-cancel]');
		if (!applyBtn && !clearBtn) {
			return;
		}

		const key = panel.getAttribute('data-panel') || '';

		if (clearBtn) {
			const wasActiveAtOpen = panel.getAttribute('data-active') === '1';

			if (!wasActiveAtOpen) {
				restorePanel(panel);
				panel.setAttribute('hidden', '');
				return;
			}

			if (key === 'price') {
				panel.querySelectorAll('input[name="max"]').forEach((i) => (i.value = ''));
				panel.querySelector('[data-price-wrap]')?.resetPriceUI?.();
				getOrCreateHidden('max').value = '';
			} else if (key === 'time') {
				panel.querySelectorAll('input[name="time[]"]').forEach((i) => (i.checked = false));
			} else if (key === 'genre') {
				panel.querySelectorAll('input[name="genre[]"]').forEach((i) => (i.checked = false));
			} else if (key === 'age_guide') {
				panel.querySelectorAll('input[name="age_guide"]').forEach((i) => (i.checked = false));
			} else if (key === 'date') {
				getOrCreateHidden('from').value = '';
				getOrCreateHidden('to').value = '';
			} else if (key === 'sort') {
				getOrCreateHidden('sort').value = 'name_asc';
				const defaultRadio = panel.querySelector('input[name="sort"][value="name_asc"], input[name="sort"][value="name-atz"]');
				if (defaultRadio) {
					defaultRadio.checked = true;
				}
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
			const params = cleanParams(paramsFromForm(), DEFAULTS);
			updateClearChip(bar, params, { sort: 'name_asc' });

			clearBtn.blur?.();
			panel.setAttribute('hidden', '');
			render();
			return;
		}

		if (applyBtn) {
			if (key === 'price') {
				const wrap = panel.querySelector('[data-price-wrap]');
				if (wrap) {
					const srcMax = wrap.querySelector('input[name="max"]');
					getOrCreateHidden('max').value = String(srcMax?.value ?? '');
				}
			} else if (key === 'sort') {
				const selected = panel.querySelector('input[name="sort"]:checked');
				getOrCreateHidden('sort').value = selected?.value || 'name_asc';
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);
			updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
			const params = cleanParams(paramsFromForm(), DEFAULTS);
			updateClearChip(bar, params, { sort: 'name_asc' });

			applyBtn.blur?.();
			panel.setAttribute('hidden', '');
			render();
		}
	});

	// Search debounce.
	const qInput = form.querySelector('input[name="q"]');
	if (qInput) {
		let timeout;
		qInput.addEventListener('input', () => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				// Skip the network call for partial queries (1–2 chars):
				// the REST endpoint would strip `q` anyway, so the request
				// would refetch the unfiltered baseline on every keystroke.
				// Empty (length 0) must still render so clearing the box
				// restores the full list; 3+ chars renders with `q` included.
				const v = (qInput.value || '').trim();
				if (v.length > 0 && !has3Chars(v)) {
					return;
				}
				setAllTriggersFromForm();
				updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
				const params = cleanParams(paramsFromForm(), DEFAULTS);
				updateClearChip(bar, params, { sort: 'name_asc' });
				render();
			}, 300);
		});
	}

	// Auto-apply on Age Guide radio change. Single-select filter — there's no
	// "Apply" step required; the radio commits immediately, the popover closes,
	// the pill repaints, and the listing re-fetches. Cancel-when-active in the
	// popover still works as the explicit clear (unsets the radio + applies).
	form.addEventListener('change', (e) => {
		const tgt = e.target;
		if (!(tgt instanceof HTMLElement) || !tgt.matches('input[name="age_guide"]')) {
			return;
		}
		const panel = bar.querySelector('[data-panel="age_guide"]');
		if (panel) {
			panel.setAttribute('hidden', '');
			// Refresh the snapshot so a subsequent reopen-then-cancel doesn't
			// restore the pre-selection state and silently "undo" the choice.
			snapshotPanel(panel);
		}
		setAllTriggersFromForm();
		paintChoiceActives(document);
		updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
		const params = cleanParams(paramsFromForm(), DEFAULTS);
		updateClearChip(bar, params, { sort: 'name_asc' });
		render();
	});

	// Prevent native form submission.
	form.addEventListener('submit', (e) => e.preventDefault());

	// Strip campaign context keys from the URL on initial load.
	const currentUrl = new URL(location.href);
	let needsClean = false;
	CONTEXT_KEYS.forEach((k) => {
		if (currentUrl.searchParams.has(k)) {
			currentUrl.searchParams.delete(k);
			needsClean = true;
		}
	});
	if (needsClean) {
		history.replaceState(
			{},
			'',
			currentUrl.search
				? `${ currentUrl.pathname }${ currentUrl.search }`
				: currentUrl.pathname
		);
	}

	// Initial state.
	paintChoiceActives(document);
	setAllTriggersFromForm();

	// Baseline the fully-seeded filter state before any render() below diffs
	// against it, so filters deep-linked via the URL are not counted as applies.
	filterTracker.init(paramsFromForm());

	const hasUrlFilters = ['q', 'from', 'to', 'time', 'max', 'genre', 'age_guide', 'show', 'sort'].some((key) => {
		const val = urlParams.get(key);
		if (!val) {
			return false;
		}
		if (key === 'sort' && val === 'name_asc') {
			return false;
		}
		return true;
	});

	const hasSSR = list?.dataset?.ssrInitial === '1' && (list.firstElementChild || (list.textContent || '').trim().length > 0);

	if (hasUrlFilters || !hasSSR) {
		updateAllFiltersCount(bar, form, { multiCsvKeys: MULTI });
		const params = cleanParams(paramsFromForm(), DEFAULTS);
		updateClearChip(bar, params, { sort: 'name_asc' });
		render(false);
	}
}
